import { useCallback, useEffect, useRef, useState } from "react";
import { IoRocketSharp } from "react-icons/io5";
import { MdHistory } from "react-icons/md";
import "./App.css";
import LoginPage from "./components/auth/LoginPage";
import Sidebar from "./components/common/Sidebar";
import HistoryTab from "./components/history/HistoryTab";
import FileUpload from "./components/input/FileUpload";
import MethodSelection from "./components/input/MethodSelection";
import ConfirmModal from "./components/modals/ConfirmModal";
import FeedbackModal from "./components/modals/FeedbackModal";
import PromptEditor from "./components/modals/PromptEditor";
import SettingsModal from "./components/modals/SettingsModal";
import ResultsSection from "./components/results/ResultsSection";
import { ENDPOINTS } from "./config/api";
import { PROMPT_CATEGORY_KEYS, PROMPT_CATEGORY_LABELS, STORAGE_KEYS } from "./config/constants";
import { useAuth } from "./contexts/AuthContext";
import { getModels } from "./services/api";
import { apiJson } from "./services/httpClient";

function App() {
  const { session, loading, signOut, profile, profileLoaded, updateProfile } = useAuth();

  const [textInput, setTextInput] = useState("");
  const [summaryResult, setSummaryResult] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState("Default");
  const [selectedModel, setSelectedModel] = useState("");
  const [temperature, setTemperature] = useState(0.0);
  const [topP, setTopP] = useState(0.05);
  const [activeTab, setActiveTab] = useState("playground");
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const [workTitle, setWorkTitle] = useState("");

  const [intendedAudience, setIntendedAudience] = useState("general");
  const [useCase, setUseCase] = useState("NUS Bulleting");
  const [summaryStyle, setSummaryStyle] = useState("concise");
  const [parser, setParser] = useState("PyMuPDF");
  const [creativity, setCreativity] = useState(0.5);
  const [maxLength, setMaxLength] = useState(4096);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedPrompt] = useState("auto");
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showImportConfirmModal, setShowImportConfirmModal] = useState(false);
  const [pendingImport, setPendingImport] = useState(null);

  // Models configuration from backend
  const [availableModels, setAvailableModels] = useState([]);
  const [modelConfig, setModelConfig] = useState({});

  // Cached prompts data
  const [cachedPrompts, setCachedPrompts] = useState(null);
  const promptsLoadingRef = useRef(false);

  // Sync theme with profile or local state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.THEME) === 'dark';
  });

  const fileUploadRef = useRef(null);
  const resultsSectionRef = useRef(null);

  // Sync profile settings when loaded
  useEffect(() => {
    if (profile) {
      if (profile.theme) {
        setIsDarkMode(profile.theme === 'dark');
      }
      if (profile.custom_prompts) {
        // We'll merge this into cachedPrompts when loaded
      }
    }
  }, [profile]);

  // Function to load and cache prompts
  const loadAndCachePrompts = useCallback(async () => {
    if (promptsLoadingRef.current || cachedPrompts) return; // Already loading or loaded

    promptsLoadingRef.current = true;
    try {
      // Use profile custom prompts if available, fall back to localStorage for migration/offline
      const savedPrompts = profile?.custom_prompts || JSON.parse(localStorage.getItem(STORAGE_KEYS.CUSTOM_PROMPTS) || "{}");

      const customPrompts = savedPrompts;

      // Fetch all prompts with full templates in a single request
      const data = await apiJson(`${ENDPOINTS.PROMPTS}?include_templates=true`);

      // Organize prompts by category
      const categorizedPrompts = {};
      for (const key of PROMPT_CATEGORY_KEYS) {
        categorizedPrompts[key] = [];
      }

      const promptsData = {};

      // Categorize prompts from the single response (no individual fetches needed)
      for (const prompt of data.prompts) {
        // Use custom prompt if available, otherwise use the template from the bulk response
        promptsData[prompt.key] = customPrompts[prompt.key] || prompt.template;

        // Add to category
        const categoryKey = prompt.category || "other";
        if (categorizedPrompts[categoryKey]) {
          categorizedPrompts[categoryKey].push({
            id: prompt.key,
            label: prompt.name,
          });
        } else {
          categorizedPrompts.other.push({
            id: prompt.key,
            label: prompt.name,
          });
        }
      }

      // Build categories array (only include non-empty categories)
      const categories = PROMPT_CATEGORY_KEYS
        .filter((key) => categorizedPrompts[key].length > 0)
        .map((key) => ({
          name: PROMPT_CATEGORY_LABELS[key],
          prompts: categorizedPrompts[key],
        }));

      // Cache the loaded data
      setCachedPrompts({
        prompts: promptsData,
        categories,
      });
    } catch (err) {
      console.error("Failed to load prompts:", err);
    } finally {
      promptsLoadingRef.current = false;
    }
  }, [cachedPrompts, profile]); // Ref still guards against double-calls while keeping the callback fresh

  // Load prompts when session is active (or cache is cleared via handleReloadPrompts)
  useEffect(() => {
    if (session && !cachedPrompts) {
      loadAndCachePrompts();
    }
  }, [session, cachedPrompts, loadAndCachePrompts]);

  // Fetch available models from backend
  useEffect(() => {
    if (loading || !session) return;

    const fetchModels = async () => {
      try {
        const data = await getModels();
        const models = data.models || [];
        setAvailableModels(models);
        setModelConfig(data.config || {});

        // Set first available model as default if not already set
        if (models.length > 0) {
          setSelectedModel((prev) => prev || models[0]);
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
        // Fallback to a safe default
        const fallbackModel = 'gpt-5-mini';
        setAvailableModels([fallbackModel]);
        setSelectedModel((prev) => prev || fallbackModel);
      }
    };

    fetchModels();
  }, [loading, session]);

  useEffect(() => {
    setCreativity(temperature);
  }, [temperature]);

  useEffect(() => {
    // Calculate max length from backend config (85% of max tokens to leave buffer)
    if (selectedModel && modelConfig[selectedModel]) {
      const maxTokens = modelConfig[selectedModel].max_output_tokens || 4096;
      setMaxLength(Math.round(maxTokens * 0.85));
    }
  }, [selectedModel, modelConfig]);

  useEffect(() => {
    const theme = isDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEYS.THEME, theme);

    // Wait for the initial profile load before writing settings back to Supabase.
    if (session && profileLoaded && profile?.theme !== theme) {
      updateProfile({ theme });
    }
  }, [isDarkMode, profile, profileLoaded, session, updateProfile]);

  const handleSavePrompts = async (prompts) => {
    // Update cached prompts
    if (cachedPrompts) {
      setCachedPrompts({
        ...cachedPrompts,
        prompts,
      });
    }

    // Save to profile
    if (session) {
      try {
        await updateProfile({ custom_prompts: prompts });
      } catch (err) {
        console.error("Failed to save prompts to profile:", err);
      }
    }

    // Fallback/Legacy
    localStorage.setItem(STORAGE_KEYS.CUSTOM_PROMPTS, JSON.stringify(prompts));
  };

  const applyImport = useCallback((summary) => {
    setSummaryResult([summary.summary]);
    setTextInput(summary.content || "");
    setWorkTitle(summary.metadata?.custom_title || "");
    setActiveTab("playground");
    setCurrentStep(3);
    setTimeout(() => {
      resultsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  const handleImportFromHistory = useCallback((summary) => {
    const hasExistingWork = summaryResult.length > 0 || isLoading;
    if (hasExistingWork) {
      setPendingImport(summary);
      setShowImportConfirmModal(true);
    } else {
      applyImport(summary);
    }
  }, [summaryResult, isLoading, applyImport]);

  const handleConfirmImport = useCallback(() => {
    if (pendingImport) {
      applyImport(pendingImport);
      setPendingImport(null);
      setShowImportConfirmModal(false);
    }
  }, [pendingImport, applyImport]);

  const handleCancelImport = useCallback(() => {
    setPendingImport(null);
    setShowImportConfirmModal(false);
  }, []);

  const handleReloadPrompts = () => {
    // Clear cache and force reload
    setCachedPrompts(null);
    promptsLoadingRef.current = false;
    // loadAndCachePrompts will be re-triggered by the effect since cachedPrompts becomes null
  };

  // Show login page if not authenticated
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!session) {
    return <LoginPage isDarkMode={isDarkMode} />;
  }

  return (
    <div className={`app-container ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        onOpenSettings={() => setShowSettingsModal(true)}
        onFeedback={() => setShowFeedbackModal(true)}
        onLogout={signOut}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        temperature={temperature}
        setTemperature={setTemperature}
        topP={topP}
        setTopP={setTopP}
        maxLength={maxLength}
        setMaxLength={setMaxLength}
        parser={parser}
        setParser={setParser}
        availableModels={availableModels}
        modelConfig={modelConfig}
        onEditPrompts={() => setShowPromptEditor(true)}
        isDarkMode={isDarkMode}
        onToggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      {showPromptEditor && (
        <PromptEditor
          isOpen={showPromptEditor}
          onClose={() => setShowPromptEditor(false)}
          cachedData={cachedPrompts}
          onReload={handleReloadPrompts}
          onSave={handleSavePrompts}
        />
      )}

      {showFeedbackModal && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
        />
      )}

      <ConfirmModal
        isOpen={showImportConfirmModal}
        onClose={handleCancelImport}
        onConfirm={handleConfirmImport}
        title="Import Summary"
        message="You have an existing summary in the playground. Importing will override your current work. Do you want to continue?"
      />

      <div className="content">
        <div className="page-header">
          <h1>
            {activeTab === 'playground' ? (
              <IoRocketSharp style={{ marginRight: '0.5rem' }} />
            ) : (
              <MdHistory style={{ marginRight: '0.5rem' }} />
            )}
            {activeTab === 'playground' ? 'Playground' : 'History'}
          </h1>
          <p>
            {activeTab === 'playground'
              ? 'Explore AI-powered document summarization'
              : 'View your summarization history'}
          </p>
        </div>

        <hr style={{ marginBottom: '2rem' }} />

        <div className={`tab-content ${activeTab === 'playground' ? 'active' : ''}`}>
          <MethodSelection
            currentStep={currentStep}
            onContinue={() => {
              setCurrentStep(2);
              setTimeout(() => {
                fileUploadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
            selectedMethod={selectedMethod}
            setSelectedMethod={setSelectedMethod}
            workTitle={workTitle}
            setWorkTitle={setWorkTitle}
            useCase={useCase}
            setUseCase={setUseCase}
            intendedAudience={intendedAudience}
            setIntendedAudience={setIntendedAudience}
            summaryStyle={summaryStyle}
            setSummaryStyle={setSummaryStyle}
            creativity={creativity}
            setCreativity={setCreativity}
            temperature={temperature}
            setTemperature={setTemperature}
            maxLength={maxLength}
            setMaxLength={setMaxLength}
            selectedModel={selectedModel}
          />
          <FileUpload
            ref={fileUploadRef}
            textInput={textInput}
            setTextInput={setTextInput}
            parser={parser}
            uploadedFile={uploadedFile}
            setUploadedFile={setUploadedFile}
            currentStep={currentStep}
            onContinue={() => {
              setCurrentStep(3);
              setTimeout(() => {
                resultsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
            onBack={() => setCurrentStep(1)}
          />

          <ResultsSection
            ref={resultsSectionRef}
            summaryResult={summaryResult}
            setSummaryResult={setSummaryResult}
            textInput={textInput}
            workTitle={workTitle}
            selectedMethod={selectedMethod}
            temperature={temperature}
            topP={topP}
            selectedModel={selectedModel}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            intendedAudience={intendedAudience}
            summaryStyle={summaryStyle}
            uploadedFile={uploadedFile}
            selectedPrompt={selectedPrompt}
            useCase={useCase}
            currentStep={currentStep}
            onBack={() => setCurrentStep(2)}
          />
        </div>

        <div className={`tab-content ${activeTab === 'history' ? 'active' : ''}`}>
          <div className="section card">
            <HistoryTab isActive={activeTab === 'history'} onImport={handleImportFromHistory} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
